import QRCode from 'qrcode.react';
import { BackButton } from '../../components/organisms/Header/HeaderMolecule';
import { ApplicationEvent } from '../../models/analytics';
import {
  useHandoffCreatorMachine,
  UseHandoffCreatorMachineProps,
} from '../hooks/useHandoffCreatorMachine';
import { LoadingOverlay } from '../../core/screens/LoadingOverlay/LoadingOverlay';
import { QrHandoff } from '../screens/handoff/QrHandoff/QrHandoff';
import { HandoffCodeScanned } from '../screens/handoff/HandoffCodeScanned/HandoffCodeScanned';
import { AwaitingQrCodeScreenWithInstructions } from '../../components/screens/AwaitingCredentialsScreen/atoms/AwaitingQrCodeScreenWithInstructions';
import { useApplicationEvent } from '../../core/Analytics/useApplicationEvent';
import { pickTextFromWithinDoubleQuotes } from '../../utilities/utils';
import { useCredentialsCopy } from '../../components/screens/SubmitCredentials/hooks/useCredentialsCopy';

export type HandoffCreatorMachineViewProps = {
  onHandoffDisable: VoidFunction;
} & BackButton &
  UseHandoffCreatorMachineProps;

export const HandoffCreatorMachineView = ({
  provider,
  onHandoffDisable,
  onBack,
  onDone,
  onHandoffUpdated,
  handoffCredentialsAction,
  providedCredentials,
}: HandoffCreatorMachineViewProps) => {
  const [current, send] = useHandoffCreatorMachine({
    provider,
    handoffCredentialsAction,
    onDone,
    onHandoffUpdated,
    providedCredentials,
  });
  const { handoffQRCode, userHintId, credentials, error } = current.context;
  const { heading } = useCredentialsCopy();

  const { sendEvent: sendApplicationEvent } = useApplicationEvent({
    applicationEvent: ApplicationEvent.HANDOFF_CREATOR_MACHINE,
  });

  if (current.matches('APPLICATION_ERROR_VIEW')) {
    sendApplicationEvent(ApplicationEvent.HANDOFF_CREATOR_MACHINE_ERROR);
    throw error;
  }

  if (current.matches('POLL_HANDOFF_SESSION_QR_CODE_VIEW')) {
    return (
      <QrHandoff
        userHintId={userHintId}
        provider={provider}
        qrCodeValue={handoffQRCode!}
        forceWebFlow={onHandoffDisable}
        onBack={onBack}
        /* todo do we want all payloads? (if yes then prop name is misleading)
         * or only for error status?
         * but when status change then we would never render anything anyway..
         */
        credentialsError={pickTextFromWithinDoubleQuotes(credentials?.statusPayload)}
      />
    );
  }

  if (current.matches('POLL_HANDOFF_SESSION_BANK_ID_VIEW')) {
    return (
      <AwaitingQrCodeScreenWithInstructions
        provider={provider}
        heading={heading}
        qrCode={
          <QRCode data-testid="handoff-qr-code" value={handoffQRCode!} size={220} includeMargin />
        }
        allowExit
      />
    );
  }

  if (current.matches('POLLING_HANDOFF_CREDENTIALS_VIEW')) {
    return (
      <HandoffCodeScanned
        provider={provider}
        credentials={credentials}
        onCancel={() => {
          send({ type: 'EVENT_CANCEL_CONSUMED_HANDOFF_SESSION' });
        }}
      />
    );
  }

  return <LoadingOverlay />;
};
